/**
 * PostHog mobile adapter. Wraps `posthog-react-native` (native + Expo web) so
 * the rest of the app stays vendor-neutral.
 *
 * SDK is loaded via lazy `require()` — if the package is not installed (e.g.
 * unit tests, CI), the adapter degrades silently and every method becomes a
 * no-op. The same client also backs error reporting (`captureException`),
 * which `../observability.ts` calls so mobile errors and product analytics
 * ride one PostHog connection — see ADR-0065.
 *
 * Configuration (read once at `init`):
 *   - `EXPO_PUBLIC_POSTHOG_KEY`   project API key (required to emit events)
 *   - `EXPO_PUBLIC_POSTHOG_HOST`  override for self-hosted / EU cloud
 *                                 (defaults to PostHog US cloud)
 */

import type {
  AnalyticsAdapter,
  AnalyticsProperties,
  AnalyticsTraits,
} from './analytics';

type PostHogClient = {
  identify(distinctId: string, properties?: Record<string, unknown> | undefined): void;
  capture(event: string, properties?: Record<string, unknown> | undefined): void;
  screen(name: string, properties?: Record<string, unknown> | undefined): void;
  reset(): void;
  flush(): Promise<void>;
  /** Routes through PostHog's Error Tracking pipeline (events of type
   *  `$exception`). Present on `posthog-react-native` ≥ 3.3; optional so
   *  a pinned older SDK degrades to a noop rather than crashing — mirrors
   *  the `posthog-node` adapter in `apps/api/src/lib/analytics-provider.ts`. */
  captureException?: (
    error: unknown,
    properties?: Record<string, unknown> | undefined,
  ) => void;
  /** PostHog RN's native opt-in/out — drops queued events SDK-side
   *  before the next network hop. The methods are present on
   *  `posthog-react-native` ≥ 3.x; older majors omitted them, so
   *  the type marks them optional and the adapter calls through
   *  defensively. */
  optIn?: () => void;
  optOut?: () => void;
};

/** Subset of `posthog-react-native`'s PostHogOptions we actually
 *  pass. `defaultOptIn: false` is the SDK-level safety net for the
 *  ADR-0065 boot opt-out: even if the adapter's gate is somehow
 *  bypassed, the SDK itself starts opted-out and only emits after an
 *  explicit `optIn()`. The flag is recognised on `posthog-react-native`
 *  ≥ 3.x; older majors silently ignore unknown options. */
type PostHogCtor = new (
  apiKey: string,
  options?: {
    host?: string;
    flushAt?: number;
    defaultOptIn?: boolean;
    /** Error Tracking autocapture (posthog-react-native ≥ 4.x). Installs
     *  the RN global handlers (`ErrorUtils` + unhandled-rejection) that
     *  route uncaught JS errors through `captureException`. This is the
     *  only place those global handlers get installed — without it, only
     *  errors we manually `reportError` (ErrorBoundary + tRPC wrapper)
     *  are captured. `nativeCrashes` additionally routes native iOS/
     *  Android crashes (native-module faults, OOM, startup crashes) through
     *  the optional `@posthog/react-native-plugin` native module — a no-op
     *  if that package isn't linked (web, Expo Go, tests). All autocaptured
     *  events — JS and native — flow through `captureException`, which
     *  respects the SDK opt-out, so they stay gated behind analytics
     *  consent per ADR-0065. */
    errorTracking?: {
      autocapture?:
        | boolean
        | {
            uncaughtExceptions?: boolean;
            unhandledRejections?: boolean;
            nativeCrashes?: boolean;
            console?: boolean | Array<'debug' | 'log' | 'info' | 'warn' | 'error'>;
          };
    };
  } | undefined,
) => PostHogClient;

function loadPostHogCtor(): PostHogCtor | null {
  // A *literal* require() so Metro statically detects `posthog-react-native`
  // and bundles it into BOTH the native and web graphs. The previous
  // `Function('return require')()` indirection hid the module from Metro, so
  // the SDK was never bundled — and because Hermes (native) and the browser
  // (web export) have no global `require` for that trick to resolve, every
  // shipped build silently degraded to a no-op. With a literal require Metro
  // follows the dependency and bundles the SDK in. In the Vitest/Node harness
  // the package's source can't be parsed and this throws — caught below, so
  // unit tests stay inert exactly as before.
  try {
    const mod = require('posthog-react-native') as {
      PostHog?: PostHogCtor | undefined;
    };
    return mod.PostHog ?? null;
  } catch {
    return null;
  }
}

function readKey(): string | undefined {
  if (typeof process === 'undefined') return undefined;
  return process.env.EXPO_PUBLIC_POSTHOG_KEY;
}

function readHost(): string | undefined {
  if (typeof process === 'undefined') return undefined;
  return process.env.EXPO_PUBLIC_POSTHOG_HOST;
}

export class PostHogAnalytics implements AnalyticsAdapter {
  private client: PostHogClient | null = null;
  private initPromise: Promise<void> | null = null;
  /** Default opt-out — see ADR-0065 boot sequence. No event reaches the
   *  SDK until the consent bridge in `_layout.tsx` calls `optIn()` after
   *  an explicit user decision. */
  private optedOut = true;

  init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = Promise.resolve().then(() => {
      const apiKey = readKey();
      if (!apiKey) return;
      const Ctor = loadPostHogCtor();
      if (!Ctor) return;
      try {
        const host = readHost();
        // `defaultOptIn: false` makes the SDK start opted-out — any
        // events the constructor might queue (auto-capture, session
        // start, identity bootstrap) are dropped at the SDK boundary
        // before our adapter-level gate even runs. The consent bridge
        // in `_layout.tsx` flips this on once the user accepts.
        this.client = new Ctor(apiKey, {
          ...(host !== undefined ? { host } : {}),
          defaultOptIn: false,
          // Install the global JS error/rejection handlers so crashes
          // outside the ErrorBoundary + tRPC wrapper are captured too, plus
          // native iOS/Android crash capture via `@posthog/react-native-
          // plugin` (autolinked; no-op on web/Expo Go/tests where the native
          // module is absent). `console: false` avoids double-reporting the
          // `[mobile-error]` lines that `observability.reportError` already
          // forwards.
          errorTracking: {
            autocapture: {
              uncaughtExceptions: true,
              unhandledRejections: true,
              nativeCrashes: true,
              console: false,
            },
          },
        });
        // Reconcile SDK state with the adapter's current opt-state.
        // The consent bridge may have called `optOut()` or `optIn()`
        // before init resolved — flush that decision through to the
        // SDK now that it exists.
        if (this.optedOut) this.client.optOut?.();
        else this.client.optIn?.();
      } catch {
        // SDK init failed (e.g. running in a Node test harness). Stay noop.
        this.client = null;
      }
    });
    return this.initPromise;
  }

  identify(userId: string, traits?: AnalyticsTraits | undefined): void {
    if (this.optedOut) return;
    this.client?.identify(userId, traits);
  }

  track(
    event: string,
    properties?: AnalyticsProperties | undefined,
    _userId?: string | null | undefined,
  ): void {
    if (this.optedOut) return;
    this.client?.capture(event, properties);
  }

  screen(name: string, properties?: AnalyticsProperties | undefined): void {
    if (this.optedOut) return;
    this.client?.screen(name, properties);
  }

  captureException(
    error: unknown,
    properties?: AnalyticsProperties | undefined,
  ): void {
    // Same opt-state gate as track() — error capture follows the
    // `analytics` consent purpose (ADR-0065 folded the old `errorReports`
    // purpose into it). When opted out, drop before the SDK boundary.
    if (this.optedOut) return;
    this.client?.captureException?.(
      error,
      properties,
    );
  }

  reset(): void {
    // `reset` clears the bound distinctId on sign-out. Allowed regardless
    // of opt-state — a user signing out should always shed identity, even
    // if they had previously withdrawn analytics consent.
    this.client?.reset();
  }

  async flush(): Promise<void> {
    await this.client?.flush();
  }

  optIn(): void {
    this.optedOut = false;
    this.client?.optIn?.();
  }

  optOut(): void {
    this.optedOut = true;
    this.client?.optOut?.();
  }

  isOptedOut(): boolean {
    return this.optedOut;
  }
}
